/**
 * Badges Integration Component.
 * Simple, clean UI for badge provider configuration.
 */

import { useState, useEffect } from 'react';
import { Row, Col, Button, Form, Table, Modal } from 'react-bootstrap';
import { IconRefresh, IconSettings, IconTrash } from '@tabler/icons-react';
import { connect } from 'react-redux';
import InsightsLoader from 'components/loaders/InsightsLoader';
import BadgeGroupConfigModal from './BadgeGroupConfigModal';
import { badgesActions } from 'pages/integrations/containers/BadgesContainer/actions';

const BadgesIntegration = ({ badgeConfigs, badgeGroups, isLoading, isGroupsLoading, dispatch }) => {
  const [provider, setProvider] = useState('credly');
  // Accredible: api_key only
  // Credly: uuid + api_key
  const [apiKey, setApiKey] = useState('');
  const [uuid, setUuid] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const config = badgeConfigs && badgeConfigs.length > 0 ? badgeConfigs[0] : null;

  const providerType = config?.provider_type || 'accredible';

  useEffect(() => {
    if (config) {
      dispatch(badgesActions.fetchBadgeGroups(providerType));
    }
  }, [config, providerType, dispatch]);

  const isFormValid = () => {
    if (provider === 'accredible') {
      return apiKey.trim();
    }
    return uuid.trim() && apiKey.trim();
  };

  const handleSaveProvider = () => {
    if (!isFormValid()) {
      return;
    }
    setIsSaving(true);

    const data = provider === 'accredible' ? { api_key: apiKey } : { uuid, api_key: apiKey };

    dispatch(
      badgesActions.createBadgeConfig(data, provider, () => {
        setIsSaving(false);
        setApiKey('');
        setUuid('');
      })
    );
  };

  const handleSync = () => {
    if (config) {
      setIsSyncing(true);
      dispatch(badgesActions.syncBadgeGroups(config.id, providerType, () => setIsSyncing(false)));
    }
  };

  const handleDelete = () => {
    if (config) {
      dispatch(badgesActions.deleteBadgeConfig(config.id, providerType));
      setShowDeleteModal(false);
    }
  };

  if (isLoading) {
    return <InsightsLoader />;
  }

  if (!config) {
    return (
      <div className="provider-setup">
        <Row className="mb-4">
          <Col>
            <h4>Badge Provider Setup</h4>
            <p className="text-muted">Connect your badge provider to issue digital credentials.</p>
          </Col>
        </Row>

        <Row>
          <Col lg="6">
            <Form.Group className="mb-3">
              <Form.Label>Provider</Form.Label>
              <Form.Select value={provider} onChange={(e) => setProvider(e.target.value)}>
                <option value="credly">Credly</option>
                <option value="accredible">Accredible</option>
              </Form.Select>
            </Form.Group>

            {provider === 'credly' && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Organization UUID <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={uuid}
                  onChange={(e) => setUuid(e.target.value)}
                  placeholder="Enter your organization UUID"
                />
                <Form.Text className="text-muted">Find your organization UUID in Credly settings.</Form.Text>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>
                API Key <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter your API key"
              />
              <Form.Text className="text-muted">
                Find your API key in your {provider === 'accredible' ? 'Accredible' : 'Credly'} account settings.
              </Form.Text>
            </Form.Group>

            <Button variant="danger" onClick={handleSaveProvider} disabled={isSaving || !isFormValid()}>
              {isSaving && <span className="spinner-border spinner-border-sm me-2" />}
              Connect Provider
            </Button>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="badges-configured">
      <Row className="mb-4 align-items-center">
        <Col>
          <h4>Badge Groups</h4>
          <p className="text-muted mb-0">
            Connected to <strong>{config.provider_type === 'accredible' ? 'Accredible' : 'Credly'}</strong>
          </p>
        </Col>
        <Col xs="auto">
          <Button variant="outline-danger" size="sm" className="me-2" onClick={handleSync} disabled={isSyncing}>
            {isSyncing ? <span className="spinner-border spinner-border-sm" /> : <IconRefresh size={16} stroke={1.5} />}
            <span className="ms-1">Sync Groups</span>
          </Button>
          <Button variant="danger" size="sm" onClick={() => setShowDeleteModal(true)}>
            <IconTrash size={16} stroke={1.5} />
            <span className="ms-1">Remove</span>
          </Button>
        </Col>
      </Row>

      <div className="table-holder">
        <Table className="dns-table align-middle">
          <thead>
            <tr>
              <th>Group Name</th>
              <th>Status</th>
              <th style={{ width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isGroupsLoading ? (
              <tr>
                <td colSpan="3" className="text-center py-4">
                  <span className="spinner-border spinner-border-sm me-2" />
                  Loading groups...
                </td>
              </tr>
            ) : badgeGroups.length === 0 ? (
              <tr>
                <td colSpan="3" className="text-center text-muted py-4">
                  No groups found. Click &quot;Sync Groups&quot; to import from{' '}
                  {config.provider_type === 'accredible' ? 'Accredible' : 'Credly'}.
                </td>
              </tr>
            ) : (
              badgeGroups.map((group) => (
                <tr key={group.id}>
                  <td>
                    <strong>{group.name}</strong>
                    {group.description && (
                      <div className="text-muted small" dangerouslySetInnerHTML={{ __html: group.description }} />
                    )}
                  </td>
                  <td>{group.is_active ? 'Enabled' : <span className="text-muted">Disabled</span>}</td>
                  <td>
                    <Button variant="outline-danger" size="sm" onClick={() => setSelectedGroup(group)}>
                      <IconSettings size={16} stroke={1.5} />
                      <span className="ms-1">Configure</span>
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <BadgeGroupConfigModal
        group={selectedGroup}
        provider={providerType}
        isOpen={Boolean(selectedGroup)}
        onClose={() => setSelectedGroup(null)}
      />

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Remove Badge Provider</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to remove this badge provider? All synced groups will be removed.</Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleDelete}>
            Remove
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

function mapStateToProps(state) {
  const { badgeConfigs, badgeGroups, isLoading, isGroupsLoading } = state.BADGES_INTEGRATION || {};
  return {
    badgeConfigs: badgeConfigs || [],
    badgeGroups: badgeGroups || [],
    isLoading: isLoading || false,
    isGroupsLoading: isGroupsLoading || false
  };
}

export default connect(mapStateToProps)(BadgesIntegration);
